import { useState, useImperativeHandle, forwardRef, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService, type K8sResource } from '@/services/k8sResourceService'
import { licenseApiService, type LicenseVerificationResponse } from '@/services/licenseApiService'
import { HttpError } from '@/lib/httpError'
import { dateLocaleFor } from '@/lib/dateLocale'
import { licensePhaseLabel, licenseTypeLabel } from '@/lib/statusLabels'

interface UpdateLicenseModalProps {
  canDirectUpdate: boolean // platform owner
  canRequestUpdate: boolean // platform owner or platform admin
  onSuccess?: () => void
}

export interface UpdateLicenseModalRef {
  open: (license: K8sResource) => void
  close: () => void
}

const UpdateLicenseModal = forwardRef<UpdateLicenseModalRef, UpdateLicenseModalProps>(
  ({ canDirectUpdate, canRequestUpdate, onSuccess }, ref) => {
    const { t, i18n } = useTranslation(['plans', 'common'])
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [verifying, setVerifying] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [license, setLicense] = useState<K8sResource | null>(null)
    const [licenseKey, setLicenseKey] = useState('')
    const [verificationResult, setVerificationResult] = useState<LicenseVerificationResponse | null>(null)
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct')
    // An expired license closes the approval queue an admin's request would
    // otherwise sit in, so the admin gets to apply a key directly. The backend
    // decides this for itself from the signed key; this only keeps the UI from
    // sending a request that can no longer be approved.
    const [renewingExpired, setRenewingExpired] = useState(false)
    const verificationResultRef = useRef<HTMLDivElement>(null)

    // A verified key that is outside its validity window: genuine signature,
    // expired or not started. It can be applied, and the panel says plainly what
    // applying it does, because the platform stops the moment it lands.
    const outsideWindow = verificationResult?.OutsideValidityWindow === true

    useImperativeHandle(ref, () => ({
      open: (licenseData: K8sResource) => {
        const phase = licenseData.status?.phase
        const expired = phase === 'Expired' || phase === 'Invalid'
        const adminOverride = !canDirectUpdate && canRequestUpdate && expired

        setLicense(licenseData)
        setRenewingExpired(adminOverride)
        setActionType(canDirectUpdate || adminOverride ? 'direct' : 'request')
        setIsOpen(true)
        setError('')
        setSuccess(false)
        setLicenseKey('')
        setVerificationResult(null)
      },
      close: () => {
        handleClose()
      },
    }))

    const handleVerify = async () => {
      if (!licenseKey.trim()) {
        setError(t('updateModal.licenseKey.required'))
        return
      }

      try {
        setVerifying(true)
        setError('')
        const result = await licenseApiService.verifyLicense(licenseKey.trim())
        setVerificationResult(result)

        // Scroll to verification result after a short delay
        setTimeout(() => {
          verificationResultRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }, 100)
      } catch (err) {
        const errorMessage = err instanceof HttpError && err.status === 429
          ? t('updateModal.rateLimited', "You're verifying too often. Please wait about 10 seconds and try again.")
          : err instanceof Error ? err.message : t('updateModal.error.verifyFailed', 'Verification failed')
        setError(errorMessage)
        setVerificationResult(null)
      } finally {
        setVerifying(false)
      }
    }

    const handleSubmit = async () => {
      if (!license || !verificationResult || loading) return

      try {
        setLoading(true)
        setError('')

        // Update the license resource with new license key
        const updatedLicense = {
          ...license,
          spec: {
            ...license.spec,
            licenseKey: licenseKey.trim(),
          },
        }

        if (actionType === 'direct') {
          // Direct update
          await k8sResourceService.updateResource(updatedLicense)
        } else {
          // Submit update request
          await k8sResourceService.requestUpdateResource(updatedLicense)
        }

        setSuccess(true)
      } catch (err) {
        const errorMessage = err instanceof HttpError && err.status === 429
          ? t('updateModal.rateLimited', "You're submitting too often. Please wait about 10 seconds and try again.")
          : err instanceof Error ? err.message : t('updateModal.error.generic', 'Update failed, please try again')
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    const handleClose = () => {
      const wasSuccess = success
      setIsOpen(false)
      setLicense(null)
      setLicenseKey('')
      setVerificationResult(null)
      setActionType('direct')
      setError('')
      setSuccess(false)

      if (wasSuccess) {
        onSuccess?.()
      }
    }

    if (!isOpen || !license) return null

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-primary">
                  {t('updateModal.title')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('updateModal.subtitle')}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading || verifying}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {actionType === 'direct'
                        ? t('updateModal.success.directTitle')
                        : t('updateModal.success.requestTitle')}
                    </p>
                    <p className="text-xs text-green-700">
                      {actionType === 'direct'
                        ? t('updateModal.success.directDesc')
                        : t('updateModal.success.requestDesc')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !success && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Current License Info */}
            {!success && (
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-sm font-semibold text-gray-900">{t('updateModal.currentLicense')}</h3>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t('updateModal.licenseInfo.name')}</p>
                    <p className="font-medium text-gray-900">{license.metadata.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t('updateModal.licenseInfo.type')}</p>
                    <p className="font-medium text-gray-900">{licenseTypeLabel(license.status?.licenseType)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t('updateModal.licenseInfo.status')}</p>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      license.status?.isValid === true ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {licensePhaseLabel(license.status?.phase)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* An admin renewing an expired license: say why the usual approval
                step is not here, so it does not read as a permissions bug. */}
            {!success && renewingExpired && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                <p className="text-sm text-amber-900">
                  {t('updateModal.expiredOverride.title')}
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  {t('updateModal.expiredOverride.detail')}
                </p>
              </div>
            )}

            {/* Action Type Selection (Show before verification for owner) */}
            {!success && canDirectUpdate && canRequestUpdate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('updateModal.actionType.label')}
                </label>
                <div className="flex gap-3">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'direct'
                        ? 'border-primary bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('updateModal.actionType.direct')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('updateModal.actionType.directDesc')}
                      </p>
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'request'
                        ? 'border-primary bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('updateModal.actionType.request')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('updateModal.actionType.requestDesc')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Admin Info Notice */}
            {!success && !canDirectUpdate && canRequestUpdate && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700">
                      {t('updateModal.adminNotice')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Verification Result - Moved here for better visibility */}
            {!success && verificationResult && (
              <div
                ref={verificationResultRef}
                className={`p-5 border-2 rounded-xl shadow-sm ${
                  outsideWindow
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                }`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    outsideWindow ? 'bg-amber-500' : 'bg-green-500'
                  }`}>
                    {outsideWindow ? (
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className={`text-base font-bold mb-1 ${outsideWindow ? 'text-amber-900' : 'text-green-900'}`}>
                      {outsideWindow
                        ? t('updateModal.verification.outsideWindow')
                        : t('updateModal.verification.success')}
                    </h4>
                    <p className={`text-sm ${outsideWindow ? 'text-amber-800' : 'text-green-700'}`}>
                      {outsideWindow
                        ? verificationResult.ErrorMessage
                        : t('updateModal.verification.successDesc')}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm bg-white/60 rounded-lg p-4">
                  <div>
                    <p className={`text-xs font-medium mb-1 ${outsideWindow ? 'text-amber-700' : 'text-green-600'}`}>{t('updateModal.verification.product')}</p>
                    <p className="text-gray-900 font-semibold">{verificationResult.Product}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${outsideWindow ? 'text-amber-700' : 'text-green-600'}`}>{t('updateModal.verification.licenseType')}</p>
                    <p className="text-gray-900 font-semibold capitalize">{verificationResult.LicenseType}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${outsideWindow ? 'text-amber-700' : 'text-green-600'}`}>{t('updateModal.verification.customerName')}</p>
                    <p className="text-gray-900 font-semibold">{verificationResult.CustomerName}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${outsideWindow ? 'text-amber-700' : 'text-green-600'}`}>{t('updateModal.verification.email')}</p>
                    <p className="text-gray-900 font-semibold truncate">{verificationResult.Email}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${outsideWindow ? 'text-amber-700' : 'text-green-600'}`}>{t('updateModal.verification.maxFlows')}</p>
                    <p className="text-gray-900 font-semibold">{verificationResult.MaxFlows}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${outsideWindow ? 'text-amber-700' : 'text-green-600'}`}>{t('updateModal.verification.daysRemaining')}</p>
                    <p className="text-gray-900 font-semibold">{verificationResult.DaysUntilExpiration} {t('updateModal.verification.days')}</p>
                  </div>
                  <div className="col-span-2">
                    <p className={`text-xs font-medium mb-1 ${outsideWindow ? 'text-amber-700' : 'text-green-600'}`}>{t('updateModal.verification.expiresAt')}</p>
                    <p className="text-gray-900 font-semibold">{new Date(verificationResult.ExpiresAt).toLocaleDateString(dateLocaleFor(i18n.language), { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>

                {/* What applying it actually does. Said before the button, not
                    after, because there is no undo from inside the console once
                    the platform has stopped. */}
                {outsideWindow && (
                  <div className="mt-4 rounded-lg bg-white/70 border border-amber-300 px-4 py-3">
                    <p className="text-sm font-semibold text-amber-900">
                      {t('updateModal.verification.outsideWindowConsequence')}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      {t('updateModal.verification.outsideWindowDetail')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* License Key Input */}
            {!success && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('updateModal.licenseKey.label')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  disabled={verifying || loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none font-mono text-sm"
                  rows={6}
                  placeholder={t('updateModal.licenseKey.placeholder')}
                />
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {t('updateModal.licenseKey.hint')}
                  </p>
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={!licenseKey.trim() || verifying || loading}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {verifying && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    <span>{verifying ? t('updateModal.licenseKey.verifying') : t('updateModal.licenseKey.verify')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
            {success ? (
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {t('updateModal.close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading || verifying}
                >
                  {t('updateModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                    outsideWindow ? 'bg-amber-600 hover:bg-amber-700' : 'bg-primary hover:bg-primary/90'
                  }`}
                  disabled={!verificationResult || loading || verifying}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {loading
                      ? t('updateModal.processing')
                      : outsideWindow
                        ? t('updateModal.submitExpired')
                        : actionType === 'direct'
                          ? t('updateModal.submit')
                          : t('updateModal.submitRequest')}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)

UpdateLicenseModal.displayName = 'UpdateLicenseModal'

export default UpdateLicenseModal
